import {
  GetMissingFieldsLevelParameter,
  OnboardingMissingFields,
  OnboardingStatus,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { queriesOnboarding } from "@query";
import { getTaxIdLabel } from "../../../shared/utils/taxId";
import { AccountType } from "../steps/CreateAccount/AccountType";
import { BusinessInformation } from "../steps/CreateAccount/BusinessInformation";
import { PersonalInformation } from "../steps/CreateAccount/PersonalInformation";
import { Representative } from "../steps/CreateAccount/Representative";
import { Review } from "../steps/CreateAccount/Review";
import { SecureAccount } from "../steps/CreateAccount/SecureAccount";
import { OnboardingContext, OnboardingModuleCreateAccount } from "../types";
import { CreateAccountContext } from "../steps/CreateAccount/types";
import { InternationalUSPersonInformation } from "../steps/CreateAccount/InternationalUSPersonInformation";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import {
  FlowStep,
  FlowStepStatus
} from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  onboardingModule: OnboardingModuleCreateAccount,
  onboardingStatus?: OnboardingStatus,
  missingDataTax?: OnboardingMissingFields,
  missingDataBanking?: OnboardingMissingFields
): FlowStepStatus => {
  const verificationLevel =
    onboardingModule.options?.verificationLevel || "Tax";

  const status =
    verificationLevel === "Tax"
      ? onboardingStatus?.verifications?.tax
      : onboardingStatus?.verifications?.banking;
  const missingData =
    verificationLevel === "Tax" ? missingDataTax : missingDataBanking;

  const usPersonStatus =
    verificationLevel === "Tax" &&
    onboardingStatus?.verifications?.profiles?.["US"]?.tax
      ? onboardingStatus?.verifications?.profiles?.["US"]?.tax
      : undefined;

  if (
    status === VerificationLevel.VERIFIED &&
    (usPersonStatus ? usPersonStatus === VerificationLevel.VERIFIED : true)
  ) {
    return "Complete";
  }

  if (
    status === VerificationLevel.PENDING ||
    status === VerificationLevel.FAILED ||
    usPersonStatus === VerificationLevel.PENDING ||
    usPersonStatus === VerificationLevel.FAILED
  ) {
    return "Pending";
  }

  const hasNoMissingFields =
    !missingData?.requiredFields || missingData?.requiredFields?.length === 0;

  const usPersonRequiredFields =
    missingData?.profiles?.["US"]?.requiredFields || [];
  const hasNoMissingFieldsUsPerson = usPersonRequiredFields.length === 0;

  if (
    (status === VerificationLevel.UPDATE_REQUIRED ||
      usPersonStatus === VerificationLevel.UPDATE_REQUIRED) &&
    hasNoMissingFields &&
    hasNoMissingFieldsUsPerson
  ) {
    return "Pending";
  }

  return "None";
};

export const getCreateAccountStepConfig = (
  onboardingModule: OnboardingModuleCreateAccount,
  context: CreateAccountContext,
  onboardingStatus?: OnboardingStatus,
  missingDataTax?: OnboardingMissingFields,
  missingDataBanking?: OnboardingMissingFields
): FlowStep => {
  const slug = "create-account";

  const steps: FlowStep[] = [];

  steps.push({
    title: "Secure account",
    slug: "secure",
    component: SecureAccount
  });

  steps.push({
    title: "Account type",
    slug: "account-type",
    component: AccountType
  });

  if (context?.accountType?.type === "Individual") {
    steps.push({
      title: "Personal information",
      slug: "personal-information",
      component: PersonalInformation
    });

    if (context?.accountType?.isUsPerson) {
      steps.push({
        title: "U.S. person information",
        slug: "international-us-person-information",
        component: InternationalUSPersonInformation
      });
    }
  } else {
    steps.push({
      title: "Business information",
      slug: "business-information",
      component: BusinessInformation
    });

    steps.push({
      title:
        onboardingModule.options?.verificationLevel === "Banking"
          ? "Representative & beneficial owner(s)"
          : "Representative",
      slug: "representative",
      component: Representative
    });
  }

  steps.push({
    title: "Review & submit",
    slug: "review",
    component: Review
  });

  const status = getStatus(
    onboardingModule,
    onboardingStatus,
    missingDataTax,
    missingDataBanking
  );

  const taxIdLabel = getTaxIdLabel(
    context?.accountType?.type || "Individual",
    context?.accountType?.country || "US"
  );

  const internationalUSPersonLabel = context?.accountType?.isUsPerson
    ? "and U.S. person SSN"
    : null;

  const taxLabel = [taxIdLabel, internationalUSPersonLabel]
    .filter(Boolean)
    .join(" ");

  return {
    title: onboardingModule.title || "Create account",
    slug,
    steps,
    status,
    result: {
      Complete: {
        title: `Your ${taxLabel} was verified`,
        description: `Your ${taxLabel} was verified. Click continue for the next step.`
      },

      Pending: {
        type: "search",
        title: `Awaiting ${taxLabel} verification`,
        description: `We were not able to verify your ${taxLabel}. We will attempt to verify it again in 24 hours and will reach out if there are any issues. Click continue for the next step.`
      }
    }
  };
};

export const useCreateAccountStep: UseCreateOnboardingStep<
  OnboardingModuleCreateAccount
> = modules => {
  const config = useOnboardingStepQueryConfig(modules, "create_account");
  const queryOnboardingStatus = queriesOnboarding.getOnboardingStatus.useQuery(
    {},
    config
  );
  const queryMissingDataTax = queriesOnboarding.getMissingFields.useQuery(
    { level: GetMissingFieldsLevelParameter.TAX },
    config
  );
  const queryMissingDataBanking = queriesOnboarding.getMissingFields.useQuery(
    { level: GetMissingFieldsLevelParameter.BANKING },
    config
  );

  return (
    onboardingModule: OnboardingModuleCreateAccount,
    contextValue: OnboardingContext
  ) => {
    return buildOnboardingStep({
      getConfig: getCreateAccountStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryOnboardingStatus,
        queryMissingDataTax,
        queryMissingDataBanking
      ]
    });
  };
};
